import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ErrorHandlerInterceptor } from '@unified-ui/interceptors';
import { ReportTabReducers } from '@unified-ui/shared-components';
import { AuthEffects, authReducer, AuthService, AuthTokensService, SharedAuthLibModule } from '@unified-ui/shared/auth-lib';
import { RouteReuseService, RouteReuseStrategyType } from '@unified-ui/shared/services';
import { provideNamedApollo } from 'apollo-angular';
import { HttpBatchLink } from 'apollo-angular/http';
import { ConfirmationService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { createApolloClientCore, createApolloClientMediascope, createApolloClientMtReporter } from './app/graphql.config';
import { PlatformModule } from './app/platform.module';
import { PlatformEffects } from './app/store/platform.effects';
import { PLATFORM_FEATURE_KEY, platformReducer } from './app/store/platform.reducer';
import { environment } from './environments/environment';
import MyPreset from './presets/mypreset';

if (environment.production) {
    window.console.log = () => {};
}

function initTokensFactory(tokenService: AuthTokensService) {
    return () => tokenService.initTokens();
}

// Bootstrap the application with the standalone AppComponent
bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(appRoutes),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: '.guideline-dark'
                }
            }
        }),
        provideNamedApollo(() => {
            const httpLink = inject(HttpBatchLink);
            return {
                reporter: createApolloClientMtReporter(httpLink),
                mediascope: createApolloClientMediascope(httpLink),
                core: createApolloClientCore(httpLink)
            };
        }),
        importProvidersFrom(
            StoreModule.forRoot({
                [PLATFORM_FEATURE_KEY]: platformReducer,
                tabs: ReportTabReducers.reportTabsReducer,
                'platform-auth': authReducer
            }),
            EffectsModule.forRoot([PlatformEffects, AuthEffects]),
            !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 25 }) : []
        ),
        { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true },
        importProvidersFrom(SharedAuthLibModule, PlatformModule),
        ConfirmationService,
        AuthService,
        // Configure RouteReuseStrategy for memory management
        {
            provide: RouteReuseStrategy,
            useFactory: (routeReuseService: RouteReuseService) => {
                return routeReuseService.createStrategy(RouteReuseStrategyType.CONFIGURABLE, {
                    // Never reuse these routes (always fresh data)
                    neverReuseRoutes: ['mediascope', 'mt-reporter/planning', 'mt-reporter/scheduling'],
                    // Always reuse these routes when possible (preserve state)
                    alwaysReuseRoutes: ['home'],
                    // Maximum number of detached routes to keep in memory
                    maxDetachedRoutes: 3
                });
            },
            deps: [RouteReuseService]
        },
        provideAppInitializer(() => {
            const tokensService = inject(AuthTokensService);
            return tokensService.initTokens();
        })
    ]
}).catch(error => console.error(error));
